import React, { useRef, useState } from 'react';
import { EVENT_TYPES, EVENT_CATEGORIES, RESPONSE_TYPES, VISIBILITY_OPTIONS, COMPLETION_ACTION_TYPES, NOTIFICATION_CHANNELS, getEventType } from '../constants/eventTypes';
import { RSVP_FIELD_DEFS, DEFAULT_RSVP_FIELDS, createCustomRsvpField, getCustomRsvpFields } from '../constants/rsvpFields';
import { DEFAULT_EXPENSE_CONFIGURATION } from '../constants/expenseFields';
import { EVENT_TEMPLATES } from '../constants/eventTemplates';
import TypeConfigPanel from './TypeConfigPanel';
import ActionTrackingSetup from './ActionTrackingSetup';
import RuleBuilder from './RuleBuilder';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import PresignedImage from '../../../components/common/PresignedImage';
import { API_URL } from '../../../config/api';
import { uploadFilesWithProgress, inferAttachmentFileType } from '../../../utils/uploadFilesWithProgress';
import { isEventImageAttachment } from '../utils/eventTimelineUtils';

const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const DISABLED_RSVP_FIELDS = {
  guestCount: { enabled: false, required: false },
  guestNames: { enabled: false, required: false },
  guestAges: { enabled: false, required: false },
  foodPreference: { enabled: false, required: false },
  drinkPreference: { enabled: false, required: false },
  customFields: [],
};

const responseRequiresReply = (type) => {
  const t = String(type || '').toUpperCase();
  return t && t !== 'NONE';
};

const availableResponseTypes = (eventType) => {
  if (eventType === 'ATTENDANCE') {
    return RESPONSE_TYPES.filter((rt) => rt.id !== 'NONE');
  }
  if (eventType === 'EXPENSE_TRACKING' || eventType === 'ACTION_TRACKING') {
    return RESPONSE_TYPES.filter((rt) => rt.id === 'NONE');
  }
  return RESPONSE_TYPES;
};

const applyResponseTypeChange = (form, nextType) => {
  const type = nextType || 'ACCEPT_REJECT';
  return {
    responsePolicy: {
      ...form.responsePolicy,
      type,
      rsvpFields: type === 'RSVP'
        ? {
          ...DEFAULT_RSVP_FIELDS,
          customFields: getCustomRsvpFields(form.responsePolicy?.rsvpFields),
        }
        : { ...DISABLED_RSVP_FIELDS },
    },
  };
};

const toDateTimeLocal = (date, time) => {
  if (!date) return '';
  const t = (time || '00:00').slice(0, 5);
  return `${date}T${t}`;
};

const fromDateTimeLocal = (value) => {
  if (!value) return { date: '', time: '' };
  const [date, timePart = ''] = String(value).split('T');
  return { date: date || '', time: (timePart || '').slice(0, 5) };
};

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const ordinalDay = (day) => {
  const n = Number(day);
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatClockLabel = (time) => {
  if (!time) return '';
  const [hhRaw, mmRaw = '0'] = String(time).slice(0, 5).split(':');
  const hh = Number(hhRaw);
  const mm = Number(mmRaw);
  if (Number.isNaN(hh)) return '';
  const h12 = hh % 12 || 12;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return mm === 0 ? `${h12}${ampm}` : `${h12}:${String(mm).padStart(2, '0')}${ampm}`;
};

/** e.g. 14th Aug 2026 10PM (Wednesday) */
const formatPrettyScheduleInstant = (date, time) => {
  if (!date) return null;
  const [y, m, d] = String(date).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  const month = dt.toLocaleDateString('en-GB', { month: 'short' });
  const weekday = dt.toLocaleDateString('en-US', { weekday: 'long' });
  const datePart = `${ordinalDay(d)} ${month} ${y}`;
  const clock = formatClockLabel(time);
  return clock ? `${datePart} ${clock} (${weekday})` : `${datePart} (${weekday})`;
};

const CalendarChipIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CHIP_TONES = {
  start: {
    button: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-600 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60',
    label: 'text-emerald-700 dark:text-emerald-300',
    value: 'text-emerald-900 dark:text-emerald-50',
    placeholder: 'text-emerald-600/80 dark:text-emerald-300/80',
    iconWrap: 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30',
  },
  end: {
    button: 'border-violet-300 bg-violet-50 hover:bg-violet-100 dark:border-violet-600 dark:bg-violet-950/50 dark:hover:bg-violet-900/60',
    label: 'text-violet-700 dark:text-violet-300',
    value: 'text-violet-900 dark:text-violet-50',
    placeholder: 'text-violet-600/80 dark:text-violet-300/80',
    iconWrap: 'bg-violet-500 text-white shadow-sm shadow-violet-500/30',
  },
};

/** Chip shows pretty datetime; uses type="datetime-local" for date + time. */
const DateTimeChipField = ({
  fieldLabel,
  date,
  time,
  onPick,
  error,
  tone = 'start',
  emptyLabel = 'Select date & time',
}) => {
  const tones = CHIP_TONES[tone] || CHIP_TONES.start;
  const inputValue = toDateTimeLocal(date, time || '00:00');
  const pretty = formatPrettyScheduleInstant(date, time || '00:00');

  return (
    <div className="min-w-0">
      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${tones.label}`}>{fieldLabel}</p>
      <div className={`rounded-xl border-2 px-3 py-3 space-y-2.5 ${tones.button}`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg ${tones.iconWrap}`}>
            <CalendarChipIcon className="w-5 h-5" />
          </span>
          <span className="min-w-0 flex-1">
            {pretty ? (
              <span className={`block text-sm font-semibold leading-snug ${tones.value}`}>{pretty}</span>
            ) : (
              <span className={`block text-sm font-medium ${tones.placeholder}`}>{emptyLabel}</span>
            )}
            <span className={`block text-[10px] font-semibold uppercase tracking-wide mt-0.5 ${tones.label}`}>
              Date-time
            </span>
          </span>
        </div>
        <input
          type="datetime-local"
          value={inputValue}
          onChange={(e) => onPick(e.target.value)}
          aria-label={fieldLabel}
          step={60}
          className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
    </div>
  );
};

const ScheduleFields = ({ form, onChange, errors = {} }) => {
  const schedule = form.schedule || {};
  const [expiryEdited, setExpiryEdited] = useState(false);

  const expiryValue = toDateTimeLocal(
    schedule.expiresAt || schedule.endDate,
    schedule.expiresAtTime || (schedule.allDay ? '23:59' : schedule.endTime),
  );

  const applyExpiryFromEnd = (next, endDate, endTime) => {
    if (expiryEdited) return next;
    return {
      ...next,
      expiresAt: endDate || '',
      expiresAtTime: schedule.allDay ? '23:59' : (endTime || ''),
    };
  };

  const setStart = (value) => {
    const { date, time } = fromDateTimeLocal(value);
    const endDate = schedule.endDate || date;
    const next = applyExpiryFromEnd(
      {
        ...schedule,
        startDate: date,
        startTime: time || schedule.startTime || '00:00',
        endDate,
      },
      endDate,
      schedule.endTime,
    );
    onChange({ schedule: next });
  };

  const setEnd = (value) => {
    const { date, time } = fromDateTimeLocal(value);
    const endDate = date || schedule.startDate;
    const endTime = time || schedule.endTime || '00:00';
    const next = applyExpiryFromEnd(
      {
        ...schedule,
        endDate,
        endTime,
      },
      endDate,
      endTime,
    );
    onChange({ schedule: next });
  };

  const setExpiry = (value) => {
    setExpiryEdited(true);
    const { date, time } = fromDateTimeLocal(value);
    onChange({
      schedule: {
        ...schedule,
        expiresAt: date,
        expiresAtTime: time,
      },
    });
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={Boolean(schedule.allDay)}
          onChange={(e) => {
            const allDay = e.target.checked;
            const endDate = schedule.endDate || schedule.startDate;
            let next = { ...schedule, allDay };
            if (!expiryEdited) {
              next = {
                ...next,
                expiresAt: endDate || '',
                expiresAtTime: allDay ? '23:59' : (schedule.endTime || ''),
              };
            }
            onChange({ schedule: next });
          }}
          className="rounded"
        />
        All day event
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DateTimeChipField
          fieldLabel="Start date & time"
          tone="start"
          date={schedule.startDate}
          time={schedule.startTime}
          onPick={setStart}
          emptyLabel="Select start date & time"
          error={errors.startDate || errors.startTime}
        />
        <DateTimeChipField
          fieldLabel="End date & time"
          tone="end"
          date={schedule.endDate}
          time={schedule.endTime}
          onPick={setEnd}
          emptyLabel="Select end date & time"
          error={errors.endTime}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={labelClass}>
          Time zone
          <select
            value={schedule.timezone}
            onChange={(e) => onChange({ schedule: { ...schedule, timezone: e.target.value } })}
            className={inputClass}
          >
            <option value="Asia/Kolkata">Asia/Kolkata</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York</option>
          </select>
        </label>
        <label className={labelClass}>
          Event expiry
          <input
            type="datetime-local"
            value={expiryValue}
            onChange={(e) => setExpiry(e.target.value)}
            className={inputClass}
          />
        </label>
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          Repeats
          <select
            value={String(schedule.recurrence?.type || 'none').toLowerCase()}
            onChange={(e) => {
              const next = e.target.value;
              const recurrence = { ...(schedule.recurrence || {}), type: next };
              if (next === 'weekly' && !(recurrence.daysOfWeek || []).length) {
                const parsed = schedule.startDate
                  ? new Date(`${schedule.startDate}T00:00:00`)
                  : new Date();
                const map = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
                recurrence.daysOfWeek = [map[parsed.getDay()]];
              }
              if (next === 'monthly' && !(recurrence.daysOfMonth || []).length) {
                const day = schedule.startDate
                  ? Number(String(schedule.startDate).slice(8, 10))
                  : new Date().getDate();
                recurrence.daysOfMonth = [day];
              }
              onChange({ schedule: { ...schedule, recurrence } });
            }}
            className={inputClass}
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </label>
        {String(schedule.recurrence?.type || '').toLowerCase() === 'weekly' && (
          <div className="flex flex-wrap gap-1.5">
            {DAYS_OF_WEEK.map((day) => {
              const selected = (schedule.recurrence?.daysOfWeek || []).includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const days = schedule.recurrence?.daysOfWeek || [];
                    const nextDays = selected ? days.filter((item) => item !== day) : [...days, day];
                    onChange({
                      schedule: {
                        ...schedule,
                        recurrence: { ...(schedule.recurrence || {}), type: 'weekly', daysOfWeek: nextDays },
                      },
                    });
                  }}
                  className={`px-2 py-1 text-xs rounded font-medium ${
                    selected
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        )}
        {String(schedule.recurrence?.type || '').toLowerCase() === 'monthly' && (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
              const selected = (schedule.recurrence?.daysOfMonth || []).includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const days = schedule.recurrence?.daysOfMonth || [];
                    const nextDays = selected ? days.filter((item) => item !== day) : [...days, day];
                    onChange({
                      schedule: {
                        ...schedule,
                        recurrence: { ...(schedule.recurrence || {}), type: 'monthly', daysOfMonth: nextDays },
                      },
                    });
                  }}
                  className={`h-8 text-xs rounded font-medium ${
                    selected
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <summary className="text-sm font-semibold cursor-pointer text-gray-700 dark:text-gray-300">Location</summary>
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            {[
              { id: 'PHYSICAL', label: 'Physical' },
              { id: 'ONLINE', label: 'Online' },
              { id: 'HYBRID', label: 'Custom' },
            ].map((lt) => (
              <button
                key={lt.id}
                type="button"
                onClick={() => onChange({ location: { ...form.location, locationType: lt.id } })}
                className={`px-3 py-1.5 text-xs rounded-lg font-semibold ${form.location.locationType === lt.id ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}
              >
                {lt.label}
              </button>
            ))}
          </div>
          {(form.location.locationType === 'PHYSICAL' || form.location.locationType === 'HYBRID') && (
            <input type="text" value={form.location.venue} onChange={(e) => onChange({ location: { ...form.location, venue: e.target.value } })} className={inputClass} placeholder="Venue name" />
          )}
          {(form.location.locationType === 'ONLINE' || form.location.locationType === 'HYBRID') && (
            <input type="url" value={form.location.meetingUrl} onChange={(e) => onChange({ location: { ...form.location, meetingUrl: e.target.value } })} className={inputClass} placeholder="Meeting URL" />
          )}
        </div>
      </details>
    </div>
  );
};

export const BasicInformationStep = ({
  form,
  onChange,
  errors = {},
  users = [],
  currentUser,
  isDm = false,
  personalMode = false,
  channelMembers = [],
  lockEventType = false,
}) => {
  const [tagInput, setTagInput] = useState('');
  const [customFieldInput, setCustomFieldInput] = useState('');
  const scheduleSectionRef = useRef(null);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) return;
    onChange({ tags: [...form.tags, t] });
    setTagInput('');
  };

  const responseTypes = availableResponseTypes(form.eventType);
  const responseType = form.responsePolicy?.type || 'ACCEPT_REJECT';

  const scrollToSchedule = () => {
    requestAnimationFrame(() => {
      scheduleSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const selectEventType = (typeId) => {
    const patch = { eventType: typeId };
    if (typeId === 'ATTENDANCE'
      && (!form.responsePolicy?.type || form.responsePolicy.type === 'NONE')) {
      Object.assign(patch, applyResponseTypeChange(form, 'RSVP'));
    }
    if (typeId === 'SPLITWISE') {
      patch.configuration = {
        currency: 'INR',
        splitMethod: 'PERCENTAGE',
        whoCanAddExpenses: 'ALL',
        fields: {
          image: { enabled: true, required: false },
        },
        ...(form.eventType === 'SPLITWISE' ? form.configuration : {}),
      };
      if (!form.category) patch.category = 'FINANCE';
    }
    if (typeId === 'EXPENSE_TRACKING') {
      Object.assign(patch, applyResponseTypeChange(form, 'NONE'));
      patch.configuration = {
        ...DEFAULT_EXPENSE_CONFIGURATION,
        ...(form.eventType === 'EXPENSE_TRACKING' ? form.configuration : {}),
      };
      if (!form.category) patch.category = 'FINANCE';
    }
    if (typeId === 'ACTION_TRACKING') {
      Object.assign(patch, applyResponseTypeChange(form, 'NONE'));
      patch.category = form.category || 'PERSONAL';
      patch.configuration = {
        trackKind: '',
        entities: [],
        entries: [],
        actionShare: false,
        ...(form.eventType === 'ACTION_TRACKING' ? form.configuration : {}),
      };
      const creatorId = currentUser?.id;
      patch.participants = {
        ...form.participants,
        mode: 'SELF',
        allowInviteOthers: true,
        optional: [],
        required: creatorId
          ? [{ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' }]
          : (form.participants?.required || []),
      };
      patch.schedule = {
        ...form.schedule,
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        expiresAt: '',
        expiresAtTime: '',
        allDay: true,
      };
    }
    if (typeId === 'POLL') {
      Object.assign(patch, applyResponseTypeChange(form, 'POLL'));
      patch.responsePolicy = {
        ...(patch.responsePolicy || form.responsePolicy),
        type: 'POLL',
        pollQuestion: form.responsePolicy?.pollQuestion || form.name || '',
        pollOptions: (form.responsePolicy?.pollOptions || ['', '']).length >= 2
          ? form.responsePolicy.pollOptions
          : ['', ''],
        allowMultiple: Boolean(form.responsePolicy?.allowMultiple),
      };
      const creatorId = currentUser?.id;
      const required = (form.participants?.required || []).filter((p) => (
        creatorId && String(p.userId) === String(creatorId)
      ));
      if (creatorId && required.length === 0) {
        required.push({ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' });
      }
      patch.participants = {
        ...form.participants,
        mode: form.contextType === 'DM' ? 'DM_AUTO' : 'SELECT',
        optional: [],
        required,
      };
    }
    patch.eventTypeChosen = true;
    onChange(patch);
    if (typeId !== 'POLL' && typeId !== 'SPLITWISE' && typeId !== 'ACTION_TRACKING') scrollToSchedule();
  };

  const typeChosen = Boolean(lockEventType || form.eventTypeChosen);
  const hideTypePicker = typeChosen || form.eventType === 'POLL' || form.eventType === 'ACTION_TRACKING';
  const compactBasic = typeChosen && form.eventType !== 'POLL' && form.eventType !== 'ACTION_TRACKING';
  const selectedType = getEventType(form.eventType);

  return (
    <div className="space-y-5">
      {!hideTypePicker && (
      <div>
        <button type="button" className="text-xs font-semibold text-primary-600 mb-3">Create from Template ▾</button>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {EVENT_TEMPLATES.slice(0, 4).map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onChange({ templateId: tpl.id })}
              className="text-left p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
            >
              <span className="text-lg">{tpl.icon}</span>
              <p className="text-xs font-semibold text-gray-900 dark:text-white mt-1">{tpl.name}</p>
            </button>
          ))}
        </div>
      </div>
      )}

      {form.eventType !== 'POLL' && (
      <label className={labelClass}>
        Event name <span className="text-rose-500">*</span>
        <input type="text" value={form.name} onChange={(e) => onChange({ name: e.target.value })} className={inputClass} placeholder="Goa Trip, Team Dinner…" />
        {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
      </label>
      )}

      {form.eventType !== 'POLL' && !compactBasic && (
      <label className={labelClass}>
        Description
        <textarea value={form.description} onChange={(e) => onChange({ description: e.target.value })} rows={3} className={inputClass} placeholder="Additional details for participants…" />
      </label>
      )}

      {!hideTypePicker ? (
      <div>
        <p className={labelClass}>Event type <span className="text-rose-500">*</span></p>
        {errors.eventType && <p className="text-xs text-rose-500 mb-1">{errors.eventType}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {EVENT_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => selectEventType(type.id)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${
                form.eventType === type.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="text-xl">{type.icon}</span>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{type.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{type.description}</p>
            </button>
          ))}
        </div>
      </div>
      ) : (compactBasic && selectedType ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {selectedType.icon} {selectedType.label}
        </p>
      ) : null)}

      {form.eventType === 'EXPENSE_TRACKING' && (
        <TypeConfigPanel
          eventType={form.eventType}
          configuration={form.configuration}
          onChange={(configuration) => onChange({ configuration })}
        />
      )}
      {form.eventType === 'SPLITWISE' && (
        <TypeConfigPanel
          eventType={form.eventType}
          configuration={form.configuration}
          onChange={(configuration) => onChange({ configuration })}
        />
      )}

      {form.eventType === 'ACTION_TRACKING' && (
        <ActionTrackingSetup
          form={form}
          onChange={onChange}
          errors={errors}
          users={users}
          currentUser={currentUser}
        />
      )}

      {form.eventType === 'POLL' && (
        <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 p-4 space-y-3">
          <label className={labelClass}>
            Poll text <span className="text-rose-500">*</span>
            <input
              type="text"
              value={form.responsePolicy.pollQuestion || ''}
              onChange={(e) => onChange({
                name: e.target.value,
                responsePolicy: { ...form.responsePolicy, pollQuestion: e.target.value },
              })}
              className={inputClass}
              placeholder="Weekend availability?"
            />
            {errors.pollQuestion && <p className="text-xs text-rose-500 mt-1">{errors.pollQuestion}</p>}
          </label>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Options</p>
            {(form.responsePolicy.pollOptions || ['', '']).map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const next = [...(form.responsePolicy.pollOptions || [])];
                    next[i] = e.target.value;
                    onChange({ responsePolicy: { ...form.responsePolicy, pollOptions: next } });
                  }}
                  className={inputClass}
                  placeholder={`Option ${i + 1}`}
                />
                {(form.responsePolicy.pollOptions || []).length > 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = (form.responsePolicy.pollOptions || []).filter((_, idx) => idx !== i);
                      onChange({ responsePolicy: { ...form.responsePolicy, pollOptions: next } });
                    }}
                    className="px-2 text-gray-400 hover:text-rose-500"
                    aria-label="Remove option"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {errors.pollOptions && <p className="text-xs text-rose-500">{errors.pollOptions}</p>}
            <button
              type="button"
              onClick={() => onChange({
                responsePolicy: {
                  ...form.responsePolicy,
                  pollOptions: [...(form.responsePolicy.pollOptions || []), ''],
                },
              })}
              className="text-xs text-primary-600 font-semibold"
            >
              + Add option
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
            <input
              type="checkbox"
              checked={Boolean(form.responsePolicy.allowMultiple)}
              onChange={(e) => onChange({
                responsePolicy: { ...form.responsePolicy, allowMultiple: e.target.checked },
              })}
              className="rounded"
            />
            People can select more than one option
          </label>
          <ParticipantsStep
            form={form}
            onChange={onChange}
            errors={errors}
            users={users}
            currentUser={currentUser}
            isDm={isDm}
            personalMode={personalMode}
            channelMembers={channelMembers}
            pollRecipients
          />
          <details className="rounded-lg border border-blue-100 dark:border-blue-900/50 bg-white/70 dark:bg-gray-900/40 p-3">
            <summary className="text-sm font-semibold cursor-pointer text-gray-700 dark:text-gray-300">
              Advanced options
            </summary>
            <div className="mt-3 space-y-3">
              <label className={labelClass}>
                Date & time
                <input
                  type="datetime-local"
                  value={toDateTimeLocal(form.schedule.startDate, form.schedule.startTime)}
                  onChange={(e) => {
                    const { date, time } = fromDateTimeLocal(e.target.value);
                    onChange({
                      schedule: {
                        ...form.schedule,
                        startDate: date,
                        startTime: time,
                        endDate: date,
                        endTime: time,
                        expiresAt: date,
                        expiresAtTime: time,
                      },
                    });
                  }}
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                Location
                <input
                  type="text"
                  value={form.location?.venue || ''}
                  onChange={(e) => onChange({
                    location: { ...form.location, locationType: 'PHYSICAL', venue: e.target.value },
                  })}
                  className={inputClass}
                  placeholder="Optional place"
                />
              </label>
            </div>
          </details>
        </div>
      )}

      {form.eventType !== 'POLL' && form.eventType !== 'ACTION_TRACKING' && (
      <>
      {form.eventType === 'SPLITWISE' ? (
      <details
        ref={scheduleSectionRef}
        id="event-wizard-schedule"
        className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3 scroll-mt-4"
      >
        <summary className="cursor-pointer">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Schedule</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Optional. Add a date only if you want one.</span>
        </summary>
        <div className="mt-3 space-y-3">
          <ScheduleFields form={form} onChange={onChange} errors={errors} />
        </div>
      </details>
      ) : (
      <div
        ref={scheduleSectionRef}
        id="event-wizard-schedule"
        className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3 scroll-mt-4"
      >
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Schedule</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">When this event happens</p>
        </div>
        <ScheduleFields form={form} onChange={onChange} errors={errors} />
      </div>
      )}

      {!compactBasic && (
      <>
      <div className="grid grid-cols-2 gap-3">
        <label className={labelClass}>
          Category
          <select value={form.category} onChange={(e) => onChange({ category: e.target.value })} className={inputClass}>
            <option value="">Select category</option>
            {EVENT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>
        <div>
          <p className={labelClass}>Tags</p>
          <div className="flex gap-2">
            <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} className={inputClass} placeholder="Add tag" />
            <button type="button" onClick={addTag} className="px-3 py-2 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-700">Add</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {form.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs">
                {tag}
                <button type="button" onClick={() => onChange({ tags: form.tags.filter((t) => t !== tag) })} className="text-gray-400 hover:text-red-500">×</button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <div>
          <p className={labelClass}>Response type <span className="text-rose-500">*</span></p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Choose how people reply. The options below update based on your selection.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {responseTypes.map((rt) => (
              <label
                key={rt.id}
                className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                  responseType === rt.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <input
                  type="radio"
                  name="responseType"
                  checked={responseType === rt.id}
                  onChange={() => onChange(applyResponseTypeChange(form, rt.id))}
                />
                <span className="text-sm">{rt.label}</span>
              </label>
            ))}
          </div>
          {errors.responseType && <p className="text-xs text-rose-500 mt-1">{errors.responseType}</p>}
        </div>

        {responseType === 'RSVP' && (
          <div className="space-y-3 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-950/20 p-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">RSVP information to collect</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Guests fill these in when they accept.
              </p>
            </div>
            <div className="space-y-2">
              {RSVP_FIELD_DEFS.map((field) => {
                const cfg = form.responsePolicy.rsvpFields?.[field.id] || { enabled: false, required: false };
                return (
                  <label
                    key={field.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(cfg.enabled)}
                      onChange={(e) => onChange({
                        responsePolicy: {
                          ...form.responsePolicy,
                          rsvpFields: {
                            ...form.responsePolicy.rsvpFields,
                            [field.id]: {
                              enabled: e.target.checked,
                              required: e.target.checked,
                            },
                          },
                        },
                      })}
                      className="rounded mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white">{field.label}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{field.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="space-y-2 pt-1">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Custom fields</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Add your own question. Guests answer it when they accept.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customFieldInput}
                  onChange={(e) => setCustomFieldInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const label = customFieldInput.trim();
                    if (!label) return;
                    const existing = getCustomRsvpFields(form.responsePolicy?.rsvpFields);
                    onChange({
                      responsePolicy: {
                        ...form.responsePolicy,
                        rsvpFields: {
                          ...form.responsePolicy.rsvpFields,
                          customFields: [...existing, createCustomRsvpField(label)],
                        },
                      },
                    });
                    setCustomFieldInput('');
                  }}
                  className={inputClass}
                  placeholder="e.g. Song request, T-shirt size"
                />
                <button
                  type="button"
                  onClick={() => {
                    const label = customFieldInput.trim();
                    if (!label) return;
                    const existing = getCustomRsvpFields(form.responsePolicy?.rsvpFields);
                    onChange({
                      responsePolicy: {
                        ...form.responsePolicy,
                        rsvpFields: {
                          ...form.responsePolicy.rsvpFields,
                          customFields: [...existing, createCustomRsvpField(label)],
                        },
                      },
                    });
                    setCustomFieldInput('');
                  }}
                  className="px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
                >
                  Add field
                </button>
              </div>
              {getCustomRsvpFields(form.responsePolicy?.rsvpFields).map((field) => (
                <div
                  key={field.id}
                  className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800"
                >
                  <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white">{field.label}</span>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => {
                        const next = getCustomRsvpFields(form.responsePolicy?.rsvpFields).map((item) => (
                          item.id === field.id ? { ...item, required: e.target.checked } : item
                        ));
                        onChange({
                          responsePolicy: {
                            ...form.responsePolicy,
                            rsvpFields: {
                              ...form.responsePolicy.rsvpFields,
                              customFields: next,
                            },
                          },
                        });
                      }}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = getCustomRsvpFields(form.responsePolicy?.rsvpFields).filter((item) => item.id !== field.id);
                      onChange({
                        responsePolicy: {
                          ...form.responsePolicy,
                          rsvpFields: {
                            ...form.responsePolicy.rsvpFields,
                            customFields: next,
                          },
                        },
                      });
                    }}
                    className="text-gray-400 hover:text-rose-500 text-lg leading-none px-1"
                    aria-label={`Remove ${field.label}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <label className={`${labelClass} pt-2 border-t border-emerald-200/80 dark:border-emerald-800/50`}>
              Response deadline
              <input
                type="datetime-local"
                value={toDateTimeLocal(form.schedule.responseDeadline, form.schedule.responseDeadlineTime)}
                onChange={(e) => {
                  const { date, time } = fromDateTimeLocal(e.target.value);
                  onChange({
                    schedule: {
                      ...form.schedule,
                      responseDeadline: date,
                      responseDeadlineTime: time,
                    },
                  });
                }}
                className={inputClass}
              />
            </label>
          </div>
        )}

        {(responseType === 'POLL' || responseType === 'MULTIPLE_CHOICE') && (
          <div className="space-y-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 p-3">
            <label className={labelClass}>
              Question
              <input
                type="text"
                value={form.responsePolicy.pollQuestion}
                onChange={(e) => onChange({ responsePolicy: { ...form.responsePolicy, pollQuestion: e.target.value } })}
                className={inputClass}
              />
              {errors.pollQuestion && <p className="text-xs text-rose-500">{errors.pollQuestion}</p>}
            </label>
            {(form.responsePolicy.pollOptions || []).map((opt, i) => (
              <input
                key={i}
                type="text"
                value={opt}
                onChange={(e) => {
                  const next = [...form.responsePolicy.pollOptions];
                  next[i] = e.target.value;
                  onChange({ responsePolicy: { ...form.responsePolicy, pollOptions: next } });
                }}
                className={inputClass}
                placeholder={`Option ${i + 1}`}
              />
            ))}
            {errors.pollOptions && <p className="text-xs text-rose-500">{errors.pollOptions}</p>}
            <button
              type="button"
              onClick={() => onChange({
                responsePolicy: {
                  ...form.responsePolicy,
                  pollOptions: [...(form.responsePolicy.pollOptions || []), ''],
                },
              })}
              className="text-xs text-primary-600 font-semibold"
            >
              + Add option
            </button>
            <div className="flex flex-wrap gap-4 text-sm pt-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.responsePolicy.allowMultiple} onChange={(e) => onChange({ responsePolicy: { ...form.responsePolicy, allowMultiple: e.target.checked } })} className="rounded" />
                Multiple selections
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.responsePolicy.anonymous} onChange={(e) => onChange({ responsePolicy: { ...form.responsePolicy, anonymous: e.target.checked } })} className="rounded" />
                Anonymous
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.responsePolicy.allowVoteChange !== false} onChange={(e) => onChange({ responsePolicy: { ...form.responsePolicy, allowVoteChange: e.target.checked } })} className="rounded" />
                Allow vote changes
              </label>
            </div>
          </div>
        )}

        {responseRequiresReply(responseType) && responseType !== 'RSVP' && (
          <label className={`${labelClass} pt-1`}>
            Response deadline
            <input
              type="datetime-local"
              value={toDateTimeLocal(form.schedule.responseDeadline, form.schedule.responseDeadlineTime)}
              onChange={(e) => {
                const { date, time } = fromDateTimeLocal(e.target.value);
                onChange({
                  schedule: {
                    ...form.schedule,
                    responseDeadline: date,
                    responseDeadlineTime: time,
                  },
                });
              }}
              className={inputClass}
            />
          </label>
        )}
      </div>
      </>
      )}
      </>
      )}
    </div>
  );
};

/** @deprecated Schedule lives inside BasicInformationStep */
export const ScheduleStep = ScheduleFields;

/** Instagram-style person chip; excluded = 25% opacity + large × centered. */
const PersonAvatarCard = ({
  user,
  excluded = false,
  onExclude,
  onInclude,
  canToggle = true,
}) => {
  if (!user) return null;
  const firstName = (user.firstName || '').trim() || (user.username || 'User').trim();
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'User';

  return (
    <div
      className={`group relative flex flex-col items-center min-w-0 ${excluded && canToggle ? 'cursor-pointer' : ''}`}
      onClick={() => {
        if (excluded && canToggle && onInclude) onInclude();
      }}
      onKeyDown={(e) => {
        if (excluded && canToggle && onInclude && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onInclude();
        }
      }}
      role={excluded && canToggle ? 'button' : undefined}
      tabIndex={excluded && canToggle ? 0 : undefined}
      title={excluded ? `Include ${firstName}` : undefined}
    >
      <div className="relative">
        <div className={excluded ? 'opacity-25' : 'opacity-100'}>
          <div
            className="rounded-full p-[2.5px]"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 35%, #8b5cf6 65%, #06b6d4 100%)',
            }}
          >
            <div className="rounded-full p-[2px] bg-white dark:bg-gray-900">
              <PresignedAvatar
                fileKey={user.profilePicture}
                cacheKey={user.id}
                name={fullName}
                initials={`${(user.firstName || user.username || 'U')[0]}${(user.lastName || '')[0] || ''}`}
                className="w-14 h-14 rounded-full object-cover block"
                fallbackClassName="w-14 h-14 rounded-full flex items-center justify-center bg-primary-500 text-white text-sm font-semibold"
              />
            </div>
          </div>
        </div>

        {excluded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
            <span className="text-4xl font-black leading-none text-rose-600 dark:text-rose-400 drop-shadow-sm select-none">
              ×
            </span>
          </div>
        )}

        {canToggle && !excluded && onExclude && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExclude();
            }}
            title={`Exclude ${firstName}`}
            aria-label={`Exclude ${firstName}`}
            className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-md z-10"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      <p className={`mt-1.5 text-xs font-medium text-center truncate w-full max-w-[4.5rem] ${excluded ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}>
        {firstName}
      </p>
      {user.external ? (
        <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
          External
        </p>
      ) : null}
    </div>
  );
};

export const ParticipantsStep = ({ form, onChange, errors = {}, users = [], currentUser, isDm, personalMode = false, channelMembers = [], pollRecipients = false }) => {
  const [query, setQuery] = useState('');
  const [externalName, setExternalName] = useState('');
  const peoplePool = personalMode ? users : (channelMembers.length ? channelMembers : users);
  const allowExternal = form.eventType === 'SPLITWISE' || form.eventType === 'EXPENSE_TRACKING';

  const selectedIds = new Set([
    ...(form.participants.required || []).map((p) => String(p.userId)),
    ...(form.participants.optional || []).map((p) => String(p.userId)),
  ]);

  const allUsers = (() => {
    const byId = new Map();
    peoplePool.forEach((u) => {
      if (u?.id != null) byId.set(String(u.id), u);
    });
    if (currentUser?.id != null) {
      byId.set(String(currentUser.id), {
        ...currentUser,
        ...(byId.get(String(currentUser.id)) || {}),
      });
    }
    const ghostFrom = (p) => {
      const external = Boolean(p.external || String(p.userId || '').startsWith('ext-'));
      return {
        id: p.userId,
        firstName: p.userName || (external ? 'External' : 'User'),
        username: external ? 'external' : 'User',
        external,
      };
    };
    (form.participants.required || []).forEach((p) => {
      if (p?.userId != null && !byId.has(String(p.userId))) {
        byId.set(String(p.userId), ghostFrom(p));
      }
    });
    (form.participants.optional || []).forEach((p) => {
      if (p?.userId != null && !byId.has(String(p.userId))) {
        byId.set(String(p.userId), ghostFrom(p));
      }
    });
    const q = query.trim().toLowerCase();
    return Array.from(byId.values()).filter((u) => {
      if (!q) return true;
      const name = `${u.firstName || ''} ${u.lastName || ''} ${u.username || ''}`.toLowerCase();
      return name.includes(q);
    });
  })();

  const includeParticipant = (user) => {
    const required = form.participants.required || [];
    const optional = (form.participants.optional || []).filter((p) => String(p.userId) !== String(user.id));
    if (required.some((p) => String(p.userId) === String(user.id))) {
      onChange({ participants: { ...form.participants, optional } });
      return;
    }
    onChange({
      participants: {
        ...form.participants,
        optional,
        required: [
          ...required,
          {
            userId: user.id,
            userName: user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username,
            role: 'REQUIRED',
            status: user.external ? 'ACCEPTED' : 'INVITED',
            ...(user.external ? { external: true } : {}),
          },
        ],
      },
    });
  };

  const excludeParticipant = (userId) => {
    if (form.participants.mode === 'EVERYONE') {
      const fullPool = (() => {
        const byId = new Map();
        peoplePool.forEach((u) => {
          if (u?.id != null) byId.set(String(u.id), u);
        });
        if (currentUser?.id != null) byId.set(String(currentUser.id), currentUser);
        return Array.from(byId.values());
      })();
      onChange({
        participants: {
          ...form.participants,
          mode: 'SELECT',
          optional: [],
          required: fullPool
            .filter((u) => String(u.id) !== String(userId))
            .map((u) => ({
              userId: u.id,
              role: 'REQUIRED',
              status: String(u.id) === String(currentUser?.id) ? 'ACCEPTED' : 'INVITED',
            })),
        },
      });
      return;
    }
    onChange({
      participants: {
        ...form.participants,
        required: (form.participants.required || []).filter((p) => String(p.userId) !== String(userId)),
        optional: (form.participants.optional || []).filter((p) => String(p.userId) !== String(userId)),
      },
    });
  };

  const addExternalPerson = () => {
    const name = String(externalName || '').trim();
    if (!name) return;
    const required = form.participants.required || [];
    const exists = required.some((p) => String(p.userName || '').trim().toLowerCase() === name.toLowerCase() && p.external);
    if (exists) {
      setExternalName('');
      return;
    }
    const userId = `ext-${(typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, '')
      : Date.now()}`;
    onChange({
      participants: {
        ...form.participants,
        required: [
          ...required,
          {
            userId,
            userName: name,
            external: true,
            role: 'REQUIRED',
            status: 'ACCEPTED',
          },
        ],
      },
    });
    setExternalName('');
  };

  return (
    <div className="space-y-4">
      {!isDm && (
        <div className="space-y-2">
          <p className={labelClass}>
            {pollRecipients ? (
              <>
                Which participants do you want to send this poll to? <span className="text-rose-500">*</span>
              </>
            ) : (
              'Who should participate?'
            )}
          </p>
          {pollRecipients && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Pick everyone, or choose specific people who should receive and vote.
            </p>
          )}
          {(pollRecipients ? ['EVERYONE', 'SELECT'] : ['EVERYONE', 'SELECT', 'ADMINS_ONLY']).map((mode) => (
            <label key={mode} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="participantMode"
                checked={form.participants.mode === mode}
                onChange={() => {
                  if (pollRecipients && mode === 'SELECT') {
                    const creatorId = currentUser?.id;
                    onChange({
                      participants: {
                        ...form.participants,
                        mode,
                        optional: [],
                        required: creatorId
                          ? [{ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' }]
                          : [],
                      },
                    });
                    return;
                  }
                  onChange({ participants: { ...form.participants, mode } });
                }}
              />
              {mode === 'EVERYONE'
                ? (personalMode ? 'Everyone in directory' : 'Everyone in this group')
                : mode === 'SELECT'
                  ? (pollRecipients ? 'Choose specific participants' : 'Select participants')
                  : 'Admins only'}
            </label>
          ))}
        </div>
      )}
      {isDm && (
        <p className="text-sm text-gray-600 dark:text-gray-400 rounded-lg bg-blue-50 dark:bg-blue-950/30 px-3 py-2">
          {pollRecipients
            ? 'This poll will be sent to the people in this chat.'
            : 'DM participants are included automatically. You can adjust people below.'}
        </p>
      )}
      {errors.participants && <p className="text-xs text-rose-500">{errors.participants}</p>}

      {(!pollRecipients || isDm || form.participants.mode === 'SELECT') && (
      <div>
        <p className={labelClass}>{pollRecipients ? 'Send this poll to' : 'All users'}</p>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people…"
          className={inputClass}
        />
        {allowExternal ? (
          <div className="mt-3 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Someone not on Workloop</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Add them as external. They are included in the split, but will not get an invite.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addExternalPerson();
                  }
                }}
                placeholder="Name"
                className={inputClass}
              />
              <button
                type="button"
                onClick={addExternalPerson}
                className="px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600"
              >
                Add
              </button>
            </div>
          </div>
        ) : null}
        <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 p-3">
          {allUsers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-3">
              {query.trim() ? 'No matches' : 'No users available'}
            </p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8 gap-x-2 gap-y-4">
              {allUsers.map((u) => {
                const mode = form.participants.mode;
                const treatedAsIncluded = mode === 'EVERYONE' || mode === 'ADMINS_ONLY';
                const excluded = treatedAsIncluded ? false : !selectedIds.has(String(u.id));
                const isSelf = currentUser?.id && String(u.id) === String(currentUser.id);
                return (
                  <PersonAvatarCard
                    key={u.id}
                    user={u}
                    excluded={excluded}
                    canToggle={!isSelf}
                    onExclude={() => excludeParticipant(u.id)}
                    onInclude={() => includeParticipant(u)}
                  />
                );
              })}
            </div>
          )}
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
          {pollRecipients
            ? 'Tap a person to include them. You stay included as the poll creator.'
            : 'Hover and click × to exclude. Click an excluded person to include them again.'}
        </p>
      </div>
      )}

      {!pollRecipients && (
      <>
      <div className="grid grid-cols-2 gap-3">
        <label className={labelClass}>
          Min participants
          <input type="number" min="0" value={form.participants.minParticipants} onChange={(e) => onChange({ participants: { ...form.participants, minParticipants: e.target.value } })} className={inputClass} />
        </label>
        <label className={labelClass}>
          Max participants
          <input type="number" min="0" value={form.participants.maxParticipants} onChange={(e) => onChange({ participants: { ...form.participants, maxParticipants: e.target.value } })} className={inputClass} />
        </label>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.participants.allowInviteOthers}
          onChange={(e) => {
            const checked = e.target.checked;
            onChange({
              participants: { ...form.participants, allowInviteOthers: checked },
              permissions: { ...form.permissions, canInviteOthers: checked },
            });
          }}
          className="rounded mt-0.5"
        />
        <span>
          <span className="font-medium text-gray-800 dark:text-gray-200">Allow participants to invite others</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Enables Copy and Forward on this event so people can share the invitation.
          </span>
        </span>
      </label>
      </>
      )}
    </div>
  );
};

export const RulesResponsesStep = ({ form, onChange }) => (
  <div className="space-y-5">
    <p className="text-sm text-gray-500 dark:text-gray-400">
      Response type is set in Basic info. Configure type-specific settings, automation rules, and visibility here.
    </p>
    <TypeConfigPanel eventType={form.eventType} configuration={form.configuration} onChange={(configuration) => onChange({ configuration })} />
    <RuleBuilder rules={form.rules} onChange={(rules) => onChange({ rules })} />
    <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <summary className="text-sm font-semibold cursor-pointer">Visibility & privacy</summary>
      <div className="mt-3 space-y-2">
        {VISIBILITY_OPTIONS.map((v) => (
          <label key={v.id} className="flex items-center gap-2 text-sm"><input type="radio" name="visibility" checked={form.visibility.scope === v.id} onChange={() => onChange({ visibility: { ...form.visibility, scope: v.id } })} />{v.label}</label>
        ))}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.visibility.showResponsesToParticipants !== false} onChange={(e) => onChange({ visibility: { ...form.visibility, showResponsesToParticipants: e.target.checked } })} className="rounded" /> Participants can see each other&apos;s responses</label>
      </div>
    </details>
  </div>
);

export const RemindersNotificationsStep = ({ form, onChange }) => (
  <div className="space-y-5">
    <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.reminderPolicy.enabled} onChange={(e) => onChange({ reminderPolicy: { ...form.reminderPolicy, enabled: e.target.checked } })} className="rounded" /> Enable reminders</label>
    {form.reminderPolicy.enabled && (
      <div className="space-y-2">
        <p className="text-xs text-gray-500">Before event</p>
        {['10m', '30m', '1h', '1d'].map((offset) => (
          <label key={offset} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={(form.reminderPolicy.offsets || []).includes(offset)} onChange={(e) => {
              const offsets = form.reminderPolicy.offsets || [];
              onChange({ reminderPolicy: { ...form.reminderPolicy, offsets: e.target.checked ? [...offsets, offset] : offsets.filter((o) => o !== offset) } });
            }} className="rounded" />
            {offset === '10m' ? '10 minutes' : offset === '30m' ? '30 minutes' : offset === '1h' ? '1 hour' : '1 day'}
          </label>
        ))}
        <label className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
          <input type="checkbox" defaultChecked className="rounded" />
          Remind participants who haven&apos;t responded (24h before)
        </label>
      </div>
    )}
    <div>
      <p className={labelClass}>Notification channels</p>
      <div className="flex flex-wrap gap-2">
        {NOTIFICATION_CHANNELS.map((ch) => (
          <label key={ch.id} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${(form.notificationPolicy.channels || []).includes(ch.id) ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-500'} ${!ch.enabled ? 'opacity-50' : ''}`}>
            <input type="checkbox" disabled={!ch.enabled} checked={(form.notificationPolicy.channels || []).includes(ch.id)} onChange={(e) => {
              const channels = form.notificationPolicy.channels || [];
              onChange({ notificationPolicy: { ...form.notificationPolicy, channels: e.target.checked ? [...channels, ch.id] : channels.filter((c) => c !== ch.id) } });
            }} className="sr-only" />
            {ch.label}{!ch.enabled ? ' (soon)' : ''}
          </label>
        ))}
      </div>
    </div>
    <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <summary className="text-sm font-semibold cursor-pointer">Permissions</summary>
      <div className="mt-3 space-y-2 text-sm">
        <label className={labelClass}>Who can modify?<select value={form.permissions.modifyPolicy} onChange={(e) => onChange({ permissions: { ...form.permissions, modifyPolicy: e.target.value } })} className={inputClass}><option value="CREATOR_ONLY">Creator only</option><option value="CREATOR_AND_ADMINS">Creator + Admins</option><option value="ALL_PARTICIPANTS">All participants</option></select></label>
        {['canAddParticipants', 'canInviteOthers', 'canAddExpenses', 'canModifyResponses', 'canCancel', 'canTriggerFollowUp'].map((key) => (
          <label key={key} className="flex items-center gap-2"><input type="checkbox" checked={form.permissions[key]} onChange={(e) => onChange({ permissions: { ...form.permissions, [key]: e.target.checked } })} className="rounded" />{key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</label>
        ))}
      </div>
    </details>
  </div>
);

export const CompletionActionsStep = ({ form, onChange }) => {
  const toggle = (type) => {
    const exists = form.completionActions.some((a) => a.type === type);
    onChange({
      completionActions: exists
        ? form.completionActions.filter((a) => a.type !== type)
        : [...form.completionActions, { type }],
    });
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">What should happen after this event is completed?</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {COMPLETION_ACTION_TYPES.map((action) => (
          <button key={action.id} type="button" onClick={() => toggle(action.id)} className={`text-left p-3 rounded-lg border text-sm font-medium transition-colors ${form.completionActions.some((a) => a.type === action.id) ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30 text-primary-800 dark:text-primary-200' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}>
            {form.completionActions.some((a) => a.type === action.id) ? '✓ ' : ''}{action.label}
          </button>
        ))}
      </div>
      {form.completionActions.length > 0 && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3 text-xs text-gray-600 dark:text-gray-400">
          <p className="font-semibold mb-2">Workflow preview</p>
          <p>Event Completed → {form.completionActions.map((a) => a.type.replace(/_/g, ' ').toLowerCase()).join(' → ')}</p>
        </div>
      )}
    </div>
  );
};

export const AttachmentsStep = ({ form, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const attachments = form.attachments || [];

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files,
        onProgress: setProgress,
      });
      const next = results.map(({ data, fileType }) => ({
        id: data.id || data.fileUrl,
        fileName: data.fileName,
        fileUrl: data.fileUrl,
        fileType: inferAttachmentFileType({ ...data, fileType: fileType || data.fileType }),
        fileSize: data.fileSize || 0,
      }));
      onChange({ attachments: [...attachments, ...next] });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (index) => {
    onChange({ attachments: attachments.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Add images or files</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Images show on the event card in chat. Other files appear in an Attachments section with download.
        </p>
      </div>

      <label
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${
          uploading
            ? 'border-primary-300 bg-primary-50/50 dark:bg-primary-950/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploading) handleFiles(e.dataTransfer.files);
        }}
      >
        <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {uploading ? `Uploading… ${progress}%` : 'Click or drop files here'}
        </span>
        <span className="text-xs text-gray-500">Images, PDFs, docs, and more</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          disabled={uploading}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>

      {error && <p className="text-xs text-rose-500">{error}</p>}

      {attachments.length > 0 && (
        <ul className="space-y-2">
          {attachments.map((att, index) => {
            const isImage = isEventImageAttachment(att);
            return (
              <li
                key={att.id || att.fileUrl || index}
                className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-2.5"
              >
                {isImage ? (
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                    <PresignedImage
                      fileKey={att.fileUrl}
                      alt={att.fileName || 'Image'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <span className="w-12 h-12 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{att.fileName || 'File'}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {isImage ? 'Image · shown on timeline' : 'File · downloadable in event'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 px-2 py-1"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export const ReviewStep = ({ form, onEditStep }) => {
  const type = getEventType(form.eventType);
  const participantCount = (form.participants?.required?.length || 0) + (form.participants?.optional?.length || 0);
  const attachmentCount = form.attachments?.length || 0;
  const sections = [
    { step: 'basic', title: 'Basic', lines: [
      `Type: ${type?.label || form.eventType}`,
      `${form.schedule.startDate} ${form.schedule.allDay ? 'All day' : `${form.schedule.startTime} – ${form.schedule.endTime}`}`,
      `Timezone: ${form.schedule.timezone}`,
      ['daily', 'weekly', 'monthly'].includes(String(form.schedule?.recurrence?.type || '').toLowerCase())
        ? `Repeats ${String(form.schedule.recurrence.type).toLowerCase()} for the next 1 month`
        : null,
      `Response: ${(form.responsePolicy?.type || 'NONE').replace(/_/g, ' ')}`,
      getCustomRsvpFields(form.responsePolicy?.rsvpFields).length
        ? `Custom RSVP fields: ${getCustomRsvpFields(form.responsePolicy.rsvpFields).map((field) => field.label).join(', ')}`
        : null,
      form.schedule?.responseDeadline ? `Reply by: ${form.schedule.responseDeadline}${form.schedule.responseDeadlineTime ? ` ${form.schedule.responseDeadlineTime}` : ''}` : null,
      form.category ? `Category: ${form.category}` : null,
      form.tags?.length ? `Tags: ${form.tags.join(', ')}` : null,
    ].filter(Boolean) },
    { step: 'participants', title: 'Participants', lines: [`${participantCount} people`] },
    { step: 'rules', title: 'Rules', lines: ['Type settings & visibility'] },
    { step: 'notifications', title: 'Reminders', lines: [form.reminderPolicy.enabled ? `Before: ${(form.reminderPolicy.offsets || []).join(', ')}` : 'No reminders'] },
    { step: 'completion', title: 'Post Complete', lines: form.completionActions.length ? form.completionActions.map((a) => a.type.replace(/_/g, ' ')) : ['None'] },
    { step: 'attachments', title: 'Attachments', lines: [attachmentCount ? `${attachmentCount} file${attachmentCount === 1 ? '' : 's'}` : 'None'] },
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{form.name || 'Untitled event'}</h3>
        {form.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{form.description}</p>}
        <div className="mt-4 space-y-3">
          {sections.map((sec) => (
            <div key={sec.step} className="flex items-start justify-between gap-2 border-t border-gray-200 dark:border-gray-700 pt-3 first:border-0 first:pt-0">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{sec.title}</p>
                {sec.lines.map((line, i) => <p key={i} className="text-sm text-gray-800 dark:text-gray-200">{line}</p>)}
              </div>
              <button type="button" onClick={() => onEditStep(sec.step)} className="text-xs font-semibold text-primary-600 shrink-0">Edit</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
